import Erase16 from '@carbon/icons-react/es/erase/16';
import Locked16 from '@carbon/icons-react/es/locked/16';
import Reset16 from '@carbon/icons-react/es/reset/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import Unlocked16 from '@carbon/icons-react/es/unlocked/16';
import { Tooltip } from 'antd';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { UserType } from 'api/models';
import {
  ResourceIdentifier,
  ResourceType,
} from 'api/resource-management/types';
import { Avatar } from 'components/atoms/avatar';
import { Menu } from 'components/atoms/dropdown';
import { PlaceholderAvatar } from 'components/molecules/placeholder-avatar';
import { UserAsLeafResource } from 'components/organisms/resource-management/resource-types';
import { NameCell } from 'components/organisms/timesheets/common/cells/name-cell';
import { fullName } from 'utils/full-name';
import { getThumbnailUrl } from 'utils/get-thumbnail-url';
import { UserRemovedFromProjectParams } from '../../listeners';
import {
  CellWrapper,
  TextWrapperWithEllipsis,
  ItemDelete,
  NameWithCompanyInfo,
} from './component';
import { useAllocationsMoreActions } from './use-allocations-more-options';
import { useUserRemovalConfirmation } from './user-removal-confirmation';

interface Props
  extends Omit<UserAsLeafResource, 'id' | 'type' | 'totalAllocation'> {
  autoAllocation: boolean;
  softAllocationsEnabled: boolean;
  removeUserFromProjectInUI: (params: UserRemovedFromProjectParams) => void;
  onDeleteAllocations: () => void;
  onConvertAllocations: (toSoft: boolean) => void;
}

const AvatarWrapper = styled.span`
  display: flex;
  align-items: center;
  margin-right: var(--spacing-02);
`;

const UserAvatarWrapper = styled.span`
  .ant-bagde {
    height: 24px;
  }
`;

export const UserCellForProjectsView: FC<Props> = ({
  firstName,
  lastName,
  photoUrl,
  company,
  userType,
  roleId,
  referenceTeam,
  isProjectOwner,
  projectId,
  userId,
  softAllocationsEnabled,
  removeUserFromProjectInUI,
  onDeleteAllocations,
  onConvertAllocations,
  autoAllocation,
}) => {
  const resourceIdentifier: ResourceIdentifier = useMemo(
    () => ({
      type: ResourceType.User,
      userId,
      roleId,
      referenceTeamId: referenceTeam?.teamId,
    }),
    [referenceTeam?.teamId, roleId, userId],
  );

  const name = useMemo(
    () => fullName({ lastName, firstName }),
    [firstName, lastName],
  );

  const { showConfirmation: showRemovalConfirmation, canRemoveMember } =
    useUserRemovalConfirmation({
      projectId,
      userId,
      roleId,
      referenceTeam,
      onConfirm: removeUserFromProjectInUI,
    });

  const {
    detailedAllocatedMinutes,
    onMenuVisibleChange,
    showDeleteAllocationsConfirmation,
    showConvertAllocationsConfirmation,
  } = useAllocationsMoreActions({
    resourceName: name,
    projectId,
    resourceIdentifier,
    onDeleteAllocations,
    onConvertAllocations,
  });
  const hideDeleteAllAllocation = autoAllocation === true;
  const showConvertAllocation =
    softAllocationsEnabled &&
    detailedAllocatedMinutes.totalAllocatedMinutes !== 0;

  return (
    <CellWrapper
      onMenuVisibleChange={onMenuVisibleChange}
      menu={
        <Menu>
          <Menu.Header>
            <span>
              <FormattedMessage id="more_actions" />
            </span>
          </Menu.Header>
          {hideDeleteAllAllocation ? null : (
            <Menu.ItemEdit
              onClick={showDeleteAllocationsConfirmation}
              disabled={detailedAllocatedMinutes.totalAllocatedMinutes === 0}
              icon={<Erase16 />}
            >
              <Tooltip
                title={
                  detailedAllocatedMinutes.totalAllocatedMinutes !==
                  0 ? null : (
                    <FormattedMessage id="resource_management.actions.allocations.delete_all.disabled" />
                  )
                }
              >
                <span>
                  <FormattedMessage id="resource_management.actions.allocations.delete_all" />
                </span>
              </Tooltip>
            </Menu.ItemEdit>
          )}
          {showConvertAllocation &&
            autoAllocation === false &&
            (detailedAllocatedMinutes.softAllocatedMinutes === 0 ? (
              <Menu.ItemEdit
                onClick={() => {
                  showConvertAllocationsConfirmation(
                    detailedAllocatedMinutes.softAllocatedMinutes === 0,
                  );
                }}
                icon={<Unlocked16 />}
              >
                <span>
                  <FormattedMessage id="resource_management.actions.allocations.change_medium.soft" />
                </span>
              </Menu.ItemEdit>
            ) : (
              <Menu.ItemEdit
                onClick={() => {
                  showConvertAllocationsConfirmation(
                    detailedAllocatedMinutes.softAllocatedMinutes === 0,
                  );
                }}
                icon={<Locked16 />}
              >
                <span>
                  <FormattedMessage id="resource_management.actions.allocations.change_medium.hard" />
                </span>
              </Menu.ItemEdit>
            ))}
          {(!hideDeleteAllAllocation || showConvertAllocation) &&
          !autoAllocation ? (
            <Menu.Divider />
          ) : null}
          <ItemDelete
            onClick={showRemovalConfirmation}
            disabled={isProjectOwner && !canRemoveMember}
            icon={referenceTeam != null ? <Reset16 /> : <TrashCan16 />}
          >
            <Tooltip
              title={
                isProjectOwner && !canRemoveMember ? (
                  <FormattedMessage
                    id="resource_management.actions.unassign_member_from_project.disabled"
                    values={{ name: <strong>{name}</strong> }}
                  />
                ) : null
              }
            >
              <span>
                {referenceTeam != null ? (
                  <>
                    {canRemoveMember ? (
                      <FormattedMessage
                        id="resource_management.actions.unassign_member_from_placeholder"
                        values={{ teamName: referenceTeam.teamName }}
                      />
                    ) : (
                      <FormattedMessage
                        id="resource_management.actions.unassign_member_from_placeholder_and_remove"
                        values={{ teamName: referenceTeam.teamName }}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {canRemoveMember ? (
                      <FormattedMessage id="resource_management.actions.remove_row_from_project" />
                    ) : (
                      <FormattedMessage id="resource_management.actions.remove_member_from_project" />
                    )}
                  </>
                )}
              </span>
            </Tooltip>
          </ItemDelete>
        </Menu>
      }
    >
      <NameCell
        logo={
          <UserAvatarWrapper>
            <Avatar
              src={getThumbnailUrl(photoUrl)}
              name={name}
              companyLogoUrl={company.companyLogoUrl}
            />
          </UserAvatarWrapper>
        }
        primaryText={
          <NameWithCompanyInfo
            name={name}
            company={company}
            showCompanyInfo={userType === UserType.Partner}
          />
        }
        secondaryText={
          referenceTeam ? (
            <>
              <Tooltip
                title={
                  <FormattedMessage id="resource_management.assigned_from_placeholder" />
                }
                placement="bottomLeft"
                arrowPointAtCenter
              >
                <AvatarWrapper>
                  <PlaceholderAvatar isCustomerRole={false} size={12} />
                </AvatarWrapper>
              </Tooltip>
              <TextWrapperWithEllipsis>
                {referenceTeam?.teamName ?? ''}
              </TextWrapperWithEllipsis>
            </>
          ) : null
        }
      />
    </CellWrapper>
  );
};
